import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { XMarkIcon } from '@heroicons/react/24/outline';

const CATEGORIAS = ['Writing', 'Listening', 'Reading', 'Speaking'];

const preguntaBase = () => ({
  texto: '', tipo_pregunta: 'multiple',
  opciones: ['', '', '', ''],
  respuesta_correcta: '',
});

export default function EditarEvaluacion({ tipo, nivel, evalId }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const tema = getTema(tipo);
  const esProfesor = ['profesor', 'admin'].includes(usuario?.rol);

  const [cargando, setCargando] = useState(true);
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [categoria, setCategoria] = useState('Writing');
  const [duracion, setDuracion] = useState('');
  const [fecha, setFecha] = useState('');
  const [preguntas, setPreguntas] = useState([preguntaBase()]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await axios.get(`${API_URL}/evaluaciones/${evalId}`);
        const ev = res.data;
        setTitulo(ev.titulo);
        setDescripcion(ev.descripcion || '');
        setCategoria(ev.categoria || 'Writing');
        setDuracion(ev.duracion_minutos ? String(ev.duracion_minutos) : '');
        setFecha(ev.fecha ? ev.fecha.split('T')[0] : '');
        setPreguntas(ev.preguntas.map(p => ({
          texto: p.texto,
          tipo_pregunta: p.tipo_pregunta,
          opciones: p.opciones,
          respuesta_correcta: p.respuesta_correcta || '',
        })));
      } catch {
        setError('Error al cargar la evaluación');
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [evalId]);

  if (!esProfesor) return <div style={{ color: tema.text, padding: '32px' }}>Sin acceso</div>;
  if (cargando) return <div style={{ minHeight: '100vh', background: tema.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tema.muted }}>Cargando...</div>;

  const agregarPregunta = () => setPreguntas([...preguntas, preguntaBase()]);
  const eliminarPregunta = (idx) => setPreguntas(preguntas.filter((_, i) => i !== idx));

  const actualizarPregunta = (idx, campo, valor) => {
    const nuevas = [...preguntas];
    nuevas[idx] = { ...nuevas[idx], [campo]: valor };
    if (campo === 'tipo_pregunta' && valor === 'verdadero_falso') {
      nuevas[idx].opciones = ['Verdadero', 'Falso'];
      nuevas[idx].respuesta_correcta = '';
    }
    if (campo === 'tipo_pregunta' && valor === 'multiple') {
      nuevas[idx].opciones = ['', '', '', ''];
      nuevas[idx].respuesta_correcta = '';
    }
    if (campo === 'tipo_pregunta' && valor === 'rellenar_huecos') {
      nuevas[idx].opciones = [];
      nuevas[idx].respuesta_correcta = '[]';
    }
    if (campo === 'texto' && nuevas[idx].tipo_pregunta === 'rellenar_huecos') {
      const numHuecos = (valor.match(/___/g) || []).length;
      const opsActuales = nuevas[idx].opciones || [];
      const ops = Array.from({ length: numHuecos }, (_, i) => opsActuales[i] || '');
      nuevas[idx].opciones = ops;
      nuevas[idx].respuesta_correcta = JSON.stringify(ops);
    }
    setPreguntas(nuevas);
  };

  const actualizarOpcion = (pIdx, oIdx, valor) => {
    const nuevas = [...preguntas];
    const opciones = [...nuevas[pIdx].opciones];
    opciones[oIdx] = valor;
    nuevas[pIdx] = { ...nuevas[pIdx], opciones };
    setPreguntas(nuevas);
  };

  const actualizarHueco = (pIdx, hIdx, valor) => {
    const nuevas = [...preguntas];
    const ops = [...(nuevas[pIdx].opciones || [])];
    ops[hIdx] = valor;
    nuevas[pIdx] = { ...nuevas[pIdx], opciones: ops, respuesta_correcta: JSON.stringify(ops) };
    setPreguntas(nuevas);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!titulo) return setError('El título es obligatorio');
    for (const p of preguntas) {
      if (!p.texto) return setError('Todas las preguntas deben tener texto');
      if (p.tipo_pregunta === 'rellenar_huecos') {
        const huecos = (p.texto.match(/___/g) || []).length;
        if (huecos === 0) return setError('Las preguntas de rellenar huecos deben contener al menos un ___ en el enunciado');
        if (p.opciones.length !== huecos || p.opciones.some(o => !o.trim()))
          return setError('Debes escribir la respuesta correcta para cada hueco');
      } else {
        if (!p.respuesta_correcta) return setError('Todas las preguntas deben tener una respuesta correcta seleccionada');
      }
    }
    setGuardando(true);
    try {
      await axios.put(`${API_URL}/evaluaciones/${evalId}`, {
        titulo, descripcion, activa: 1, categoria,
        duracion_minutos: duracion ? parseInt(duracion) : null,
        preguntas,
        fecha: fecha || null,
      });
      navigate(`/${tipo}/${nivel}/actividades`);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px' };
  const labelStyle = { color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' };

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in" style={{ maxWidth: '780px' }}>
            <div style={{ marginBottom: '28px' }}>
              <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>✏️ Editar Evaluación</h2>
              <p style={{ color: tema.muted, fontSize: '14px' }}>Modifica el test y guarda los cambios</p>
            </div>

            {error && <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '8px', padding: '12px', color: '#e74c3c', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}

            <form onSubmit={handleGuardar}>
              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div className="form-grid-2">
                  <div>
                    <label style={labelStyle}>Título de la evaluación *</label>
                    <input style={inputStyle} value={titulo} onChange={e => setTitulo(e.target.value)} required placeholder="Ej: Test Unidad 3 - Grammar" />
                  </div>
                  <div>
                    <label style={labelStyle}>Categoría *</label>
                    <select style={inputStyle} value={categoria} onChange={e => setCategoria(e.target.value)}>
                      {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Tiempo límite (opcional)</label>
                    <select style={inputStyle} value={duracion} onChange={e => setDuracion(e.target.value)}>
                      <option value="">Sin límite</option>
                      <option value="5">5 minutos</option>
                      <option value="10">10 minutos</option>
                      <option value="15">15 minutos</option>
                      <option value="20">20 minutos</option>
                      <option value="30">30 minutos</option>
                      <option value="45">45 minutos</option>
                      <option value="60">60 minutos</option>
                      <option value="90">90 minutos</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>📅 Fecha del examen (opcional)</label>
                    <input type="date" style={inputStyle} value={fecha} onChange={e => setFecha(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Descripción (opcional)</label>
                  <input style={inputStyle} value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Instrucciones o descripción del test" />
                </div>
              </div>

              {preguntas.map((p, pIdx) => (
                <div key={pIdx} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ color: tema.accent, fontWeight: 700, fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>
                      Pregunta {pIdx + 1}
                    </span>
                    {preguntas.length > 1 && (
                      <button type="button" onClick={() => eliminarPregunta(pIdx)}
                        style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <XMarkIcon style={{ width: 14, height: 14 }} /> Eliminar
                      </button>
                    )}
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={labelStyle}>Tipo de pregunta</label>
                    <select style={inputStyle} value={p.tipo_pregunta} onChange={e => actualizarPregunta(pIdx, 'tipo_pregunta', e.target.value)}>
                      <option value="multiple">Opción múltiple (4 opciones)</option>
                      <option value="verdadero_falso">Verdadero / Falso</option>
                      <option value="rellenar_huecos">Rellenar huecos con palabras</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={labelStyle}>Enunciado de la pregunta *</label>
                    {p.tipo_pregunta === 'rellenar_huecos' && (
                      <p style={{ color: tema.accent, fontSize: '12px', marginBottom: '6px' }}>
                        Usa ___ (tres guiones bajos) para marcar cada hueco que el alumno debe rellenar
                      </p>
                    )}
                    <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={p.texto} onChange={e => actualizarPregunta(pIdx, 'texto', e.target.value)} required />
                  </div>

                  {p.tipo_pregunta === 'rellenar_huecos' ? (
                    <div style={{ marginBottom: '14px' }}>
                      {(p.texto.match(/___/g) || []).length === 0 ? (
                        <p style={{ color: tema.muted, fontSize: '13px', fontStyle: 'italic' }}>
                          Escribe el enunciado con ___ para que aparezcan los campos de respuesta
                        </p>
                      ) : (
                        <>
                          <label style={{ ...labelStyle, marginBottom: '10px' }}>Respuesta correcta para cada hueco</label>
                          {Array.from({ length: (p.texto.match(/___/g) || []).length }, (_, hIdx) => (
                            <div key={hIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <span style={{ color: tema.muted, fontSize: '13px', minWidth: '68px' }}>Hueco {hIdx + 1}:</span>
                              <input
                                style={{ ...inputStyle, flex: 1 }}
                                value={p.opciones[hIdx] || ''}
                                onChange={e => actualizarHueco(pIdx, hIdx, e.target.value)}
                                placeholder={`Palabra correcta para el hueco ${hIdx + 1}`}
                              />
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ ...labelStyle, marginBottom: '10px' }}>Opciones — selecciona la correcta (●)</label>
                      {p.opciones.map((op, oIdx) => (
                        <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <input
                            type="radio"
                            name={`correcta-${pIdx}`}
                            checked={p.respuesta_correcta === op && op !== ''}
                            onChange={() => actualizarPregunta(pIdx, 'respuesta_correcta', op)}
                            style={{ accentColor: tema.accent, width: '16px', height: '16px' }}
                          />
                          {p.tipo_pregunta === 'verdadero_falso' ? (
                            <span style={{ color: tema.text, fontSize: '14px', padding: '8px 14px', background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '6px', flex: 1 }}>{op}</span>
                          ) : (
                            <input
                              style={{ ...inputStyle, flex: 1 }}
                              value={op}
                              onChange={e => {
                                actualizarOpcion(pIdx, oIdx, e.target.value);
                                if (p.respuesta_correcta === p.opciones[oIdx])
                                  actualizarPregunta(pIdx, 'respuesta_correcta', e.target.value);
                              }}
                              placeholder={`Opción ${oIdx + 1}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <button type="button" onClick={agregarPregunta}
                style={{ width: '100%', background: `${tema.accent}11`, border: `2px dashed ${tema.accent}44`, color: tema.accent, padding: '14px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '24px' }}>
                + Añadir pregunta
              </button>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={guardando}
                  style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                  {guardando ? 'Guardando...' : '💾 Guardar cambios'}
                </button>
                <button type="button" onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                  style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '12px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
